import React, {useId, type ReactNode} from 'react'
import clsx from 'clsx'

export interface ChatPanelProps {
  /** Font Awesome icon class after the chevron, e.g. `fa-globe`. */
  icon: string
  title: ReactNode
  /** Short text at the right end of the header (e.g. a failure count). */
  meta?: ReactNode
  open: boolean
  onToggle: () => void
  /** Names the region; defaults to none (the heading names it). */
  ariaLabel?: string
  className?: string
  /** Extra class on the body. */
  detailsClassName?: string
  testId?: string
  children?: ReactNode
}

/**
 * A collapsible panel of an assistant turn: Working, Files changed, Knowledge Base Sources and Web
 * Sources all use it, so they share one header (chevron, icon, title) and one compact style
 * (`.omics-chat-panel` in ai-chat-v2.scss). Accordion pattern: a heading holding the toggle
 * button. Controlled, so a caller can force it open (Working opens while the turn runs).
 */
export const ChatPanel: React.FC<ChatPanelProps> = ({
  icon,
  title,
  meta,
  open,
  onToggle,
  ariaLabel,
  className,
  detailsClassName,
  testId,
  children,
}) => {
  const detailsId = useId()
  return (
    <section
      className={clsx('collapsible-section omics-chat-panel', className)}
      data-open={open}
      aria-label={ariaLabel}
      data-testid={testId}
    >
      <h4 className='omics-chat-panel__heading'>
        <button
          type='button'
          className='section-summary ai-chat-tool-summary omics-chat-panel__summary'
          aria-expanded={open}
          aria-controls={open ? detailsId : undefined}
          onClick={onToggle}
        >
          <i
            className={`fas collapsible-chevron ${open ? 'fa-chevron-down' : 'fa-chevron-right'}`}
            aria-hidden
          />
          <i className={`fas ${icon} omics-chat-panel__icon`} aria-hidden />
          <span className='ai-chat-tool-summary-text'>
            <span className='ai-chat-tool-summary-title'>{title}</span>
          </span>
          {meta ? <span className='ai-chat-tool-summary-meta'>{meta}</span> : null}
        </button>
      </h4>
      {open ? (
        <div
          id={detailsId}
          className={clsx('section-details omics-chat-panel__details', detailsClassName)}
        >
          {children}
        </div>
      ) : null}
    </section>
  )
}
